import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../../../foundation/altzero_base/base/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../../foundation/altzero_base/base/components/ui/card";
import { Badge } from "../../../foundation/altzero_base/base/components/ui/badge";
import { Progress } from "../../../foundation/altzero_base/base/components/ui/progress";
import {
  FileText,
  MapPin,
  Zap,
  Star,
  BarChart3,
  Calendar,
  Loader2,
  Sparkles,
  Rocket,
  Building2,
  Phone,
  Activity,
  Target,
  ChevronDown,
  ChevronUp,
  Info,
  CheckCircle,
  Settings
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../../../foundation/altzero_base/base/components/ui/dialog";

import { useDocumentForm } from "../contexts/DocumentFormContext";
import { StepProps } from "../types/document-form";
import { WorkflowNavigation } from "./WorkflowNavigation";
import { Site } from "../types/siteTypes";
import { Offer } from "../types/offerTypes";

import { supabase } from "../../../foundation/altzero_base/base/utils/supabaseClient";
import { getTenderProposalManifest } from "../services/tenderService";

const DocumentOverviewStep: React.FC<StepProps> = ({ onBack }) => {
  const navigate = useNavigate();
  const {
    clientData,
    tenderDetails,
    sites,
    offers,
    selectedTemplate,
    isGenerating,
    generationProgress,
    generationStatus,
    tenderId,
    user
  } = useDocumentForm();

  const [showGenerationDialog, setShowGenerationDialog] = useState(false);
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    client: true,
    tender: true,
    sites: true
  });

  // DB-fetched data — source of truth for the overview display
  const [dbTender, setDbTender] = useState<any>(null);
  const [dbClient, setDbClient] = useState<any>(null);
  const [dbContact, setDbContact] = useState<any>(null);

  useEffect(() => {
    if (!tenderId) return;
    let cancelled = false;
    const fetchOverviewData = async () => {
      try {
        // Fetch full tender record with client
        const { data: tenderRow } = await supabase
          .from('tender')
          .select(`
            *,
            client:client_id (
              id, name, abn, channel_partner, sf_account_id, extn
            )
          `)
          .eq('id', tenderId)
          .single();

        if (cancelled || !tenderRow) return;
        setDbTender(tenderRow);

        const clientRecord = tenderRow.client;
        if (clientRecord) {
          setDbClient(clientRecord);
          // Fetch primary contact for the client
          const { data: contactRow } = await supabase
            .from('contacts')
            .select('first_name, last_name, email, phone')
            .eq('client_id', clientRecord.id)
            .order('created_at', { ascending: true })
            .limit(1)
            .maybeSingle();
          if (!cancelled && contactRow) {
            setDbContact(contactRow);
          }
        }
      } catch (err) {
        console.error('DocumentOverviewStep: failed to fetch DB data', err);
      }
    };
    fetchOverviewData();
    return () => { cancelled = true; };
  }, [tenderId]);

  // Helpers to resolve display values — prefer DB data, fall back to context
  const clientName = dbClient?.name || clientData.name || 'Not specified';
  const clientAbn = dbClient?.abn || (clientData as any).abn || 'Not specified';
  const clientChannelPartner = dbClient?.channel_partner || (clientData as any).channel_partner || 'Not specified';
  const clientIndustry = dbClient?.extn?.industry || clientData.industry || 'Not specified';
  const clientEmail = dbContact?.email || dbClient?.extn?.email || clientData.email || 'Not specified';
  const clientPhone = dbContact?.phone || dbClient?.extn?.phone || clientData.phone || 'Not specified';
  const clientContactPerson = dbContact
    ? [dbContact.first_name, dbContact.last_name].filter(Boolean).join(' ')
    : clientData.contactPerson || 'Not specified';

  const tenderType = dbTender?.type || tenderDetails?.type || 'Not specified';
  const tenderAccountName = dbTender?.account_name || tenderDetails?.account_name || 'Not specified';
  const tenderEntityName = dbTender?.entity_name || tenderDetails?.entity_name || 'Not specified';
  const tenderAbn = dbTender?.abn || tenderDetails?.abn || 'Not specified';
  const tenderAcn = dbTender?.acn || tenderDetails?.acn || 'Not specified';
  const tenderDueAt = dbTender?.due_at || tenderDetails?.due_at;
  const tenderContractExpiry = dbTender?.current_contract_expiry || tenderDetails?.current_contract_expiry;
  const tenderCurrentRetailer = dbTender?.current_retailer || tenderDetails?.current_retailer || 'Not specified';
  const tenderMeterProvider = dbTender?.current_meter_provider || tenderDetails?.current_meter_provider || 'Not specified';
  const tenderDmaProvider = dbTender?.dma_provider || tenderDetails?.dma_provider || 'Not specified';
  const tenderRetailerComms = dbTender?.retailer_comms || tenderDetails?.retailer_comms || 'Not specified';
  const tenderCreateDma = dbTender?.create_dma ?? tenderDetails?.create_dma ?? false;
  const tenderRequestBills = dbTender?.request_bills ?? tenderDetails?.request_bills ?? false;
  const tenderAcquireCed = dbTender?.acquire_ced ?? tenderDetails?.acquire_ced ?? false;
  const tenderRequestMeterData = dbTender?.request_meter_data ?? tenderDetails?.request_meter_data ?? false;
  const tenderNotes = dbTender?.notes || tenderDetails?.notes;

  // 🆕 Streaming state
  const [streamingTemplate, setStreamingTemplate] = useState<any>(null);
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamProgress, setStreamProgress] = useState(0);
  const [streamMessage, setStreamMessage] = useState('');
  const [streamComplete, setStreamComplete] = useState(false);
  const [generatedFilePath, setGeneratedFilePath] = useState('');

  // 🆕 Generation mode: Combined (one document, all sites) vs Separate (one per site).
  // Separate reuses the SAME single-site-shaped output the backend already produces
  // when scoped to one site (see /tender/generate-proposal/stream siteId param) —
  // no separate template or pipeline needed, just N calls to the same endpoint.
  const [generationMode, setGenerationMode] = useState<'combined' | 'separate'>('combined');
  type SiteGenStatus = {
    status: 'pending' | 'generating' | 'done' | 'failed';
    progress: number;
    message: string;
    filePath?: string;
    error?: string;
  };
  const [siteGenStatus, setSiteGenStatus] = useState<Record<string, SiteGenStatus>>({});
  const [isGeneratingAllSeparate, setIsGeneratingAllSeparate] = useState(false);

  // Rehydrate from tender_proposal_document on mount/return, so navigating away to
  // view a proposal and coming back doesn't reset the per-site list to blank
  // (siteGenStatus is otherwise pure in-memory React state).
  useEffect(() => {
    if (!tenderId || sites.length === 0) return;
    let cancelled = false;
    getTenderProposalManifest(tenderId).then(result => {
      if (cancelled || !result.success || result.data.length === 0) return;
      setSiteGenStatus(prev => {
        const next = { ...prev };
        for (const entry of result.data) {
          if (entry.mode !== 'separate' || !entry.siteId) continue;
          const matchingSite = sites.find(s => (s.siteId || s.id) === entry.siteId);
          if (!matchingSite || next[matchingSite.id]?.status === 'generating') continue;
          next[matchingSite.id] = {
            status: 'done',
            progress: 100,
            message: 'Ready',
            filePath: entry.filePath,
          };
        }
        return next;
      });
    });
    return () => { cancelled = true; };
  }, [tenderId, sites]);

  // Data calculations
  const totalOffers = offers.length;
  const totalSites = sites.length;
  const totalAnnualUsage = sites.reduce((total, site) => {
    const peak = site.annualKwhPeak || 0;
    const offpeak = site.annualKwhOffpeak || 0;
    const shoulder = site.annualKwhShoulder || 0;
    return total + peak + offpeak + shoulder;
  }, 0);

  // Helper functions
  const formatUsage = (usage: number) => {
    return `${usage.toLocaleString()} kWh/year`;
  };

  const toggleSection = (section: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Streaming proposal generation with live updates
  const handleGenerateClickStream = async () => {
    const currentTenderId = tenderId;
    const userId = user?.id;

    if (!currentTenderId || !selectedTemplate || !userId) {
      console.error("Missing required data for proposal generation");
      return;
    }

    setIsStreaming(true);
    setStreamProgress(0);
    setStreamMessage('Starting...');
    setStreamingTemplate(null);
    setStreamComplete(false);
    setShowGenerationDialog(true);

    try {
      const response = await fetch('/api/scopingai/tender/generate-proposal/stream', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tenderId: currentTenderId, templateId: selectedTemplate, userId }),
      });

      if (!response.ok || !response.body) {
        throw new Error('Stream failed to start');
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let receivedCompleted = false;  // Track locally since React state is async

      while (true) {
        const { done, value } = await reader.read();
        if (done) {
          console.log('📡 SSE stream ended (done=true)');
          break;
        }

        buffer += decoder.decode(value, { stream: true });

        // Split on double-newline (SSE event boundary)
        const chunks = buffer.split('\n\n');
        buffer = chunks.pop() || '';

        for (const chunk of chunks) {
          if (!chunk.trim()) continue;

          // ROBUST SSE PARSING: Parse line-by-line instead of fragile regex
          // SSE format: "event: <name>\ndata: <json>"
          let eventName = '';
          let dataStr = '';

          const lines = chunk.split('\n');
          for (const line of lines) {
            if (line.startsWith('event: ')) {
              eventName = line.substring(7).trim();
            } else if (line.startsWith('data: ')) {
              // Append to dataStr (SSE spec allows multiple data: lines)
              dataStr += (dataStr ? '\n' : '') + line.substring(6);
            }
          }

          if (!eventName || !dataStr) {
            console.log('⚠️ SSE chunk missing event or data:', { eventName, dataStrLen: dataStr.length, chunk: chunk.substring(0, 100) });
            continue;
          }

          // Skip heartbeat events silently
          if (eventName === 'heartbeat') continue;

          console.log(`📡 SSE event: "${eventName}" (data: ${dataStr.length} chars)`);

          let data: any = {};
          try {
            data = JSON.parse(dataStr.trim());
          } catch (parseErr) {
            console.error(`❌ SSE JSON parse failed for event "${eventName}":`, parseErr, 'data preview:', dataStr.substring(0, 200));
            continue;
          }

          switch (eventName) {
            case 'progress':
              setStreamProgress(data.progress || 0);
              setStreamMessage(data.message || '');
              break;
            case 'template_skeleton':
              console.log('📋 Received template skeleton (pre-processing preview)');
              setStreamingTemplate(data.template);
              break;
            case 'chunk_ready':
              setStreamProgress(data.progress || 0);
              break;
            case 'completed':
              receivedCompleted = true;
              console.log('🎉 SSE completed event received:', JSON.stringify(data));
              setStreamProgress(100);

              // Check if template was empty
              if (data.isEmpty) {
                setStreamMessage('⚠️ Template is empty - please add content to your template!');
                alert('⚠️ WARNING: The selected template has no blocks/content!\n\nPlease:\n1. Go to Templates page\n2. Edit the template\n3. Add text, tables, or other content blocks\n4. Save the template\n5. Try generating again');
                setIsStreaming(false);
                setShowGenerationDialog(false);
              } else {
                setStreamMessage('Proposal ready! Redirecting...');
                setGeneratedFilePath(data.filePath);
                setStreamComplete(true);
                setIsStreaming(false);

                console.log('📄 Completed data:', { filePath: data.filePath, tenderId: currentTenderId });

                // Update tender status (don't block navigation on this)
                supabase.from('tender').update({
                  status: 'tender_ready',
                  updated_at: new Date().toISOString()
                }).eq('id', currentTenderId).then(({ error: statusErr }) => {
                  if (statusErr) console.warn('Failed to update tender status:', statusErr);
                  else console.log('✅ Tender status updated');
                });

                // Navigate to proposal viewer after brief delay to show "Ready!" message
                const targetUrl = `/tender/proposal-viewer?tender=${currentTenderId}&file=${encodeURIComponent(data.filePath)}&return=documents`;
                console.log('🔗 Will navigate to:', targetUrl);

                // Show "Proposal Ready!" in dialog for 1 second, then navigate
                setTimeout(() => {
                  setShowGenerationDialog(false);
                  navigate(targetUrl);
                  console.log('✅ React Router navigate() called');

                  // Safety: if still on same page after 2s, force redirect
                  setTimeout(() => {
                    if (window.location.pathname !== '/tender/proposal-viewer') {
                      console.warn('⚠️ navigate() did not work, forcing window.location redirect');
                      window.location.href = targetUrl;
                    }
                  }, 2000);
                }, 1000);
              }
              break;
            case 'error':
              console.error('❌ Streaming error from server:', data.message);
              setIsStreaming(false);
              setStreamingTemplate(null);
              setShowGenerationDialog(false);
              alert(`Proposal generation failed: ${data.message || 'Unknown error'}`);
              break;
            default:
              console.log(`ℹ️ Unknown SSE event: "${eventName}"`);
              break;
          }
        }
      }
      // Safety net: if stream ended but we never got a 'completed' event
      if (!receivedCompleted) {
        console.warn('⚠️ SSE stream ended without completed event');
        setIsStreaming(false);
        setShowGenerationDialog(false);
        // Check if there's a file path from buffer remnants
        if (buffer.trim()) {
          console.log('⚠️ Remaining buffer:', buffer.substring(0, 200));
        }
      }
    } catch (err) {
      console.error('❌ Generation failed:', err);
      setIsStreaming(false);
      setStreamingTemplate(null);
      setShowGenerationDialog(false);
      alert(`Proposal generation failed: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  };

  // 🆕 Separate mode: generate ONE site's own standalone document. Same SSE endpoint
  // as Combined, just with siteId set — the backend scopes offers/cost data to that
  // site alone and uses its own ABN on the cover page (see route's siteId handling).
  // Writes progress into siteGenStatus[site.id] instead of the shared streaming state,
  // so multiple sites can be tracked independently in the per-site list UI.
  const runSiteGeneration = async (site: Site): Promise<void> => {
    const currentTenderId = tenderId;
    const userId2 = user?.id;
    if (!currentTenderId || !selectedTemplate || !userId2) return;

    setSiteGenStatus(prev => ({
      ...prev,
      [site.id]: { status: 'generating', progress: 0, message: 'Starting...' },
    }));

    try {
      const response = await fetch('/api/scopingai/tender/generate-proposal/stream', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tenderId: currentTenderId,
          templateId: selectedTemplate,
          userId: userId2,
          siteId: site.siteId || site.id, // real site.id — see siteTypes.ts comment on the aliasing
        }),
      });

      if (!response.ok || !response.body) {
        throw new Error('Stream failed to start');
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let receivedCompleted = false;
      let resultFilePath = '';
      let resultError = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const chunks = buffer.split('\n\n');
        buffer = chunks.pop() || '';

        for (const chunk of chunks) {
          if (!chunk.trim()) continue;

          let eventName = '';
          let dataStr = '';
          for (const line of chunk.split('\n')) {
            if (line.startsWith('event: ')) eventName = line.substring(7).trim();
            else if (line.startsWith('data: ')) dataStr += (dataStr ? '\n' : '') + line.substring(6);
          }
          if (!eventName || !dataStr || eventName === 'heartbeat') continue;

          let data: any = {};
          try {
            data = JSON.parse(dataStr.trim());
          } catch {
            continue;
          }

          if (eventName === 'progress') {
            setSiteGenStatus(prev => ({
              ...prev,
              [site.id]: { status: 'generating', progress: data.progress || 0, message: data.message || '' },
            }));
          } else if (eventName === 'completed') {
            receivedCompleted = true;
            if (data.isEmpty) {
              resultError = 'Template has no content';
            } else {
              resultFilePath = data.filePath;
            }
          } else if (eventName === 'error') {
            resultError = data.message || 'Unknown error';
          }
        }
      }

      if (resultError || !receivedCompleted) {
        setSiteGenStatus(prev => ({
          ...prev,
          [site.id]: { status: 'failed', progress: 0, message: '', error: resultError || 'Generation did not complete' },
        }));
      } else {
        setSiteGenStatus(prev => ({
          ...prev,
          [site.id]: { status: 'done', progress: 100, message: 'Ready', filePath: resultFilePath },
        }));
      }
    } catch (err) {
      setSiteGenStatus(prev => ({
        ...prev,
        [site.id]: { status: 'failed', progress: 0, message: '', error: err instanceof Error ? err.message : 'Unknown error' },
      }));
    }
  };

  // Generate every site sequentially — one at a time, not in parallel, so failures are
  // isolated per site and the AI/API isn't hit with N simultaneous requests.
  const handleGenerateAllSeparate = async () => {
    setIsGeneratingAllSeparate(true);
    try {
      for (const site of sites) {
        await runSiteGeneration(site);
      }
    } finally {
      setIsGeneratingAllSeparate(false);
    }
  };

  const viewSiteProposal = (site: Site) => {
    const status = siteGenStatus[site.id];
    if (!status?.filePath || !tenderId) return;
    const realSiteId = site.siteId || site.id;
    navigate(`/tender/proposal-viewer?tender=${tenderId}&file=${encodeURIComponent(status.filePath)}&site=${realSiteId}&return=documents`);
  };

  return (
    <div className="w-full space-y-8">
      {/* Hero Section */}
      <div className="text-center space-y-4">
        <div className="flex items-center justify-center gap-3 mb-4">
          <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
            <Rocket className="w-6 h-6 text-white" />
          </div>
          <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
            Tender Overview
          </h1>
        </div>
      </div>

      {/* Summary Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="border-2 border-blue-100 bg-blue-50/30">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <Building2 className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-xs text-gray-600">Client</p>
                <p className="font-semibold text-gray-900 text-sm truncate">{clientName}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-2 border-green-100 bg-green-50/30">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <MapPin className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-xs text-gray-600">Sites</p>
                <p className="font-semibold text-gray-900 text-sm">{totalSites} Locations</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-2 border-purple-100 bg-purple-50/30">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <BarChart3 className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-xs text-gray-600">Offers</p>
                <p className="font-semibold text-gray-900 text-sm">{totalOffers} Analyzed</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-2 border-amber-100 bg-amber-50/30">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                <Zap className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-xs text-gray-600">Total Usage</p>
                <p className="font-semibold text-gray-900 text-sm">{formatUsage(totalAnnualUsage)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Detailed Information Sections */}
      <div className="space-y-6">

        {/* Client Information Section */}
        <Card className="border-2 border-blue-100">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Building2 className="w-5 h-5 text-blue-600" />
                Client Information
                <Badge variant="outline" className="ml-2">Step 1</Badge>
              </CardTitle>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => toggleSection('client')}
                className="h-8 w-8 p-0"
              >
                {expandedSections.client ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </Button>
            </div>
          </CardHeader>
          {expandedSections.client && (
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <div className="space-y-3">
                  <h4 className="font-medium text-gray-900 flex items-center gap-2">
                    <Building2 className="w-4 h-4 text-blue-600" />
                    Company Details
                  </h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Company Name:</span>
                      <span className="font-medium">{clientName}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Industry:</span>
                      <span className="font-medium">{clientIndustry}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Contact Person:</span>
                      <span className="font-medium">{clientContactPerson}</span>
                    </div>
                  </div>
                </div>

                <div className="space-y-3">
                  <h4 className="font-medium text-gray-900 flex items-center gap-2">
                    <Phone className="w-4 h-4 text-green-600" />
                    Contact Information
                  </h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Email:</span>
                      <span className="font-medium">{clientEmail}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Phone:</span>
                      <span className="font-medium">{clientPhone}</span>
                    </div>
                  </div>
                </div>

                <div className="space-y-3">
                  <h4 className="font-medium text-gray-900 flex items-center gap-2">
                    <Info className="w-4 h-4 text-purple-600" />
                    Registration
                  </h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">ABN:</span>
                      <span className="font-medium">{clientAbn}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Channel Partner:</span>
                      <span className="font-medium">{clientChannelPartner}</span>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          )}
        </Card>

        {/* Tender Details Section */}
        <Card className="border-2 border-purple-100">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-purple-600" />
                Tender Details
                <Badge variant="outline" className="ml-2">Step 2</Badge>
              </CardTitle>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => toggleSection('tender')}
                className="h-8 w-8 p-0"
              >
                {expandedSections.tender ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </Button>
            </div>
          </CardHeader>
          {expandedSections.tender && (
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <div className="space-y-3">
                  <h4 className="font-medium text-gray-900 flex items-center gap-2">
                    <Target className="w-4 h-4 text-purple-600" />
                    Tender Information
                  </h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Type:</span>
                      <span className="font-medium">{tenderType}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Account Name:</span>
                      <span className="font-medium">{tenderAccountName}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Entity Name:</span>
                      <span className="font-medium">{tenderEntityName}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">ABN:</span>
                      <span className="font-medium">{tenderAbn}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">ACN:</span>
                      <span className="font-medium">{tenderAcn}</span>
                    </div>
                  </div>
                </div>

                <div className="space-y-3">
                  <h4 className="font-medium text-gray-900 flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-green-600" />
                    Important Dates
                  </h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Due Date:</span>
                      <span className="font-medium">{tenderDueAt ? new Date(tenderDueAt).toLocaleDateString() : 'Not specified'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Contract Expiry:</span>
                      <span className="font-medium">{tenderContractExpiry ? new Date(tenderContractExpiry).toLocaleDateString() : 'Not specified'}</span>
                    </div>
                  </div>
                </div>

                <div className="space-y-3">
                  <h4 className="font-medium text-gray-900 flex items-center gap-2">
                    <Settings className="w-4 h-4 text-amber-600" />
                    Current Providers
                  </h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Current Retailer:</span>
                      <span className="font-medium">{tenderCurrentRetailer}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Meter Provider:</span>
                      <span className="font-medium">{tenderMeterProvider}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">DMA Provider:</span>
                      <span className="font-medium">{tenderDmaProvider}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Retailer Comms:</span>
                      <span className="font-medium">{tenderRetailerComms}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Flags row */}
              <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                <h4 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-600" />
                  Requirements &amp; Flags
                </h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                  {[
                    { label: 'Create DMA', value: tenderCreateDma },
                    { label: 'Request Bills', value: tenderRequestBills },
                    { label: 'Acquire CED', value: tenderAcquireCed },
                    { label: 'Request Meter Data', value: tenderRequestMeterData },
                  ].map(({ label, value }) => (
                    <div key={label} className="flex items-center gap-2">
                      <div className={`w-4 h-4 rounded-full flex-shrink-0 ${value ? 'bg-green-500' : 'bg-gray-300'}`} />
                      <span className={value ? 'text-gray-900 font-medium' : 'text-gray-500'}>{label}</span>
                    </div>
                  ))}
                </div>
              </div>

              {tenderNotes && (
                <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                  <h4 className="font-medium text-gray-900 mb-2">Notes</h4>
                  <p className="text-sm text-gray-700">{tenderNotes}</p>
                </div>
              )}
            </CardContent>
          )}
        </Card>

        {/* Sites Overview Section */}
        <Card className="border-2 border-green-100">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <MapPin className="w-5 h-5 text-green-600" />
                Sites Overview
                <Badge variant="outline" className="ml-2">Step 3</Badge>
                <Badge variant="secondary" className="ml-1">{totalSites} Sites</Badge>
              </CardTitle>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => toggleSection('sites')}
                className="h-8 w-8 p-0"
              >
                {expandedSections.sites ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </Button>
            </div>
          </CardHeader>
          {expandedSections.sites && (
            <CardContent className="space-y-4">
              {sites.length === 0 ? (
                <div className="text-center py-8">
                  <MapPin className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">No sites have been added yet.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {sites.map((site: Site, index: number) => {
                    // Get offers for this site. Matches BOTH siteId conventions on purpose:
                    // site.id is the tender_site junction id (what mapOffersWithSites-corrected
                    // offers carry) while site.siteId is the real site table PK (what raw
                    // getOffers() returns). Unlike Compare & Select and Offer Details, this
                    // step has no re-map effect of its own, so an uncorrected offers array
                    // would silently render every per-site list empty on multi-site tenders.
                    const siteOffers = offers.filter(
                      (offer: Offer) => offer.siteId === site.id || offer.siteId === site.siteId
                    );

                    return (
                      <Card key={site.id} className="border border-gray-200">
                        <CardContent className="p-4">
                          {/* Site Information */}
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                            <div className="space-y-2">
                              <h4 className="font-medium text-gray-900 flex items-center gap-2">
                                <Building2 className="w-4 h-4 text-green-600" />
                                {site.name || `Site ${index + 1}`}
                              </h4>
                              <div className="text-sm space-y-1">
                                <div className="flex justify-between">
                                  <span className="text-gray-600">NMI:</span>
                                  <span className="font-medium">{site.nmi || 'Not specified'}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-gray-600">ABN:</span>
                                  <span className="font-medium">{site.abn || 'Not specified'}</span>
                                </div>
                              </div>
                            </div>

                            <div className="space-y-2">
                              <h4 className="font-medium text-gray-900 flex items-center gap-2">
                                <MapPin className="w-4 h-4 text-blue-600" />
                                Location
                              </h4>
                              <div className="text-sm">
                                <p className="text-gray-700">
                                  {[site.address?.street, site.address?.city, site.address?.state, site.address?.postcode]
                                    .filter(Boolean)
                                    .join(', ') || 'Address not specified'}
                                </p>
                              </div>
                            </div>

                            <div className="space-y-2">
                              <h4 className="font-medium text-gray-900 flex items-center gap-2">
                                <Zap className="w-4 h-4 text-amber-600" />
                                Energy Usage
                              </h4>
                              <div className="text-sm space-y-1">
                                <div className="flex justify-between">
                                  <span className="text-gray-600">Peak:</span>
                                  <span className="font-medium">{site.annualKwhPeak ? `${site.annualKwhPeak.toLocaleString()} kWh` : 'N/A'}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-gray-600">Off-peak:</span>
                                  <span className="font-medium">{site.annualKwhOffpeak ? `${site.annualKwhOffpeak.toLocaleString()} kWh` : 'N/A'}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-gray-600">Shoulder:</span>
                                  <span className="font-medium">{site.annualKwhShoulder ? `${site.annualKwhShoulder.toLocaleString()} kWh` : 'N/A'}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-gray-600">Total:</span>
                                  <span className="font-medium">{
                                    (() => {
                                      const total = (site as any).annualKwhTotal
                                        || ((site.annualKwhPeak || 0) + (site.annualKwhOffpeak || 0) + (site.annualKwhShoulder || 0));
                                      return total ? `${total.toLocaleString()} kWh` : 'N/A';
                                    })()
                                  }</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-gray-600">Peak Demand:</span>
                                  <span className="font-medium">{site.peakDemandKw ? `${site.peakDemandKw.toLocaleString()} kW` : 'N/A'}</span>
                                </div>
                              </div>
                            </div>

                            <div className="space-y-2">
                              <h4 className="font-medium text-gray-900 flex items-center gap-2">
                                <Activity className="w-4 h-4 text-purple-600" />
                                Status
                              </h4>
                              <div className="text-sm space-y-1">
                                <Badge variant={site.isValid ? "default" : "secondary"} className="text-xs">
                                  {site.isValid ? "Complete" : "Incomplete"}
                                </Badge>
                                {site.currentRetailer && (
                                  <p className="text-gray-600">Current: {site.currentRetailer}</p>
                                )}
                              </div>
                            </div>
                          </div>

                          {/* Site Offers */}
                          {siteOffers.length > 0 && (
                            <div className="border-t border-gray-200 pt-4">
                              <h5 className="font-medium text-gray-900 flex items-center gap-2 mb-3">
                                <BarChart3 className="w-4 h-4 text-amber-600" />
                                Energy Offers ({siteOffers.length})
                              </h5>
                              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                                {siteOffers.map((offer: Offer) => (
                                  <div key={offer.id} className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                                    <div className="flex items-center justify-between mb-2">
                                      <h6 className="font-medium text-sm text-gray-900">
                                        {offer.retailerName || 'Unknown Retailer'}
                                      </h6>
                                      {offer.recommendedMonths && offer.recommendedMonths.length > 0 && (
                                        <Badge className="bg-green-100 text-green-800 border-green-200 text-xs">
                                          <Star className="w-3 h-3 mr-1 fill-current" />
                                          Recommended
                                        </Badge>
                                      )}
                                    </div>
                                    <div className="text-xs space-y-1">
                                      <div className="flex justify-between">
                                        <span className="text-gray-600">Terms:</span>
                                        <span className="font-medium">
                                          {offer.availableTerms && offer.availableTerms.length > 0
                                            ? offer.availableTerms.map(t => `${t}m`).join(', ')
                                            : 'Not set'}
                                        </span>
                                      </div>
                                      <div className="flex justify-between">
                                        <span className="text-gray-600">Green Energy:</span>
                                        <span className="font-medium">{offer.greenPercent || 0}%</span>
                                      </div>
                                      <div className="flex justify-between">
                                        <span className="text-gray-600">Status:</span>
                                        <span className="font-medium capitalize">{offer.status || 'draft'}</span>
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              )}
            </CardContent>
          )}
        </Card>

      </div>

      {/* Generation Options */}
      <Card className="border-2 border-blue-100">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-blue-600" />
            {dbTender?.status === 'tender_ready' ? 'Regenerate Tender Proposal' : 'Generate Your Tender Proposal'}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {dbTender?.status === 'tender_ready' && (
            <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <Info className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-amber-800">A proposal already exists for this tender</p>
                <p className="text-sm text-amber-700 mt-1">
                  Regenerating will replace the existing proposal with a new one based on the current data.
                </p>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="text-center p-4 bg-blue-50 rounded-lg">
              <CheckCircle className="w-8 h-8 text-blue-600 mx-auto mb-2" />
              <h4 className="font-medium text-blue-900">Data Collected</h4>
              <p className="text-sm text-blue-700">All required information gathered</p>
            </div>
            <div className="text-center p-4 bg-green-50 rounded-lg">
              <CheckCircle className="w-8 h-8 text-green-600 mx-auto mb-2" />
              <h4 className="font-medium text-green-900">Template Ready</h4>
              <p className="text-sm text-green-700">Professional template selected</p>
            </div>
            <div className="text-center p-4 bg-purple-50 rounded-lg">
              <Rocket className="w-8 h-8 text-purple-600 mx-auto mb-2" />
              <h4 className="font-medium text-purple-900">Ready to Generate</h4>
              <p className="text-sm text-purple-700">All systems ready for proposal</p>
            </div>
          </div>

          {/* Mode selector — only meaningful for multi-site tenders. Single-site tenders
              skip this entirely: Combined and Separate would produce the identical
              single document, so there's nothing to choose between. */}
          {sites.length > 1 && (
            <div className="flex items-center justify-center gap-6 p-4 bg-gray-50 rounded-lg mb-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="generationMode"
                  checked={generationMode === 'combined'}
                  onChange={() => setGenerationMode('combined')}
                  disabled={isStreaming || isGeneratingAllSeparate}
                />
                <span className="text-sm font-medium text-gray-900">Combined — one document, all sites</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="generationMode"
                  checked={generationMode === 'separate'}
                  onChange={() => setGenerationMode('separate')}
                  disabled={isStreaming || isGeneratingAllSeparate}
                />
                <span className="text-sm font-medium text-gray-900">Separate — one document per site</span>
              </label>
            </div>
          )}

          {generationMode === 'combined' || sites.length <= 1 ? (
            <div className="flex justify-center">
              <Button
                onClick={handleGenerateClickStream}
                disabled={isGenerating || isStreaming}
                className="h-auto p-6 flex flex-col items-center gap-3 bg-gradient-to-r from-primary to-primary/90 hover:from-primary/90 hover:to-primary/80 min-w-[300px]"
              >
                <div className="flex items-center gap-2">
                  <Rocket className="w-6 h-6" />
                  <span className="font-semibold text-lg">
                    {dbTender?.status === 'tender_ready' ? 'Regenerate Proposal' : 'Generate Proposal'}
                  </span>
                </div>
                <p className="text-sm text-primary-foreground/80 text-center">
                  {dbTender?.status === 'tender_ready'
                    ? 'Replace existing proposal with updated data'
                    : 'Create your comprehensive tender proposal with all collected data'}
                </p>
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {sites.map((site: Site) => {
                const s = siteGenStatus[site.id];
                return (
                  <div key={site.id} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                    <div className="min-w-0">
                      <p className="font-medium text-gray-900 text-sm truncate">{site.name || 'Unnamed site'}</p>
                      <p className="text-xs text-gray-500">NMI: {site.nmi || 'Not specified'}</p>
                    </div>
                    <div className="flex items-center gap-3 flex-shrink-0">
                      {!s || s.status === 'pending' ? (
                        <Badge variant="outline" className="text-gray-500">Pending</Badge>
                      ) : s.status === 'generating' ? (
                        <div className="flex items-center gap-2 w-40">
                          <Progress value={s.progress} className="w-24" />
                          <span className="text-xs text-gray-500">{s.progress}%</span>
                        </div>
                      ) : s.status === 'done' ? (
                        <Badge className="bg-green-100 text-green-800 border-green-200">✓ Done</Badge>
                      ) : (
                        <Badge variant="destructive">✗ Failed</Badge>
                      )}

                      {s?.status === 'done' ? (
                        <Button size="sm" variant="outline" onClick={() => viewSiteProposal(site)}>View</Button>
                      ) : (
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={s?.status === 'generating' || isGeneratingAllSeparate}
                          onClick={() => runSiteGeneration(site)}
                        >
                          {s?.status === 'failed' ? 'Retry' : 'Generate'}
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}

              <div className="flex justify-center pt-2">
                <Button
                  onClick={handleGenerateAllSeparate}
                  disabled={isGeneratingAllSeparate || sites.length === 0}
                  className="min-w-[220px]"
                >
                  {isGeneratingAllSeparate ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Rocket className="w-4 h-4 mr-2" />
                      Generate All
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Navigation */}
      <WorkflowNavigation
        onBack={onBack}
        showNextButton={false}
        backButtonText="Back to Templates"
      />

      {/* Generation Progress Dialog */}
      <Dialog open={showGenerationDialog} onOpenChange={setShowGenerationDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {streamComplete ? (
                <FileText className="w-5 h-5 text-green-600" />
              ) : (
                <Loader2 className="w-5 h-5 animate-spin text-blue-600" />
              )}
              {streamComplete ? 'Proposal Ready!' : 'Generating Proposal'}
            </DialogTitle>
            <DialogDescription>
              {streamComplete
                ? 'Your proposal has been generated. Redirecting to viewer...'
                : 'Creating your comprehensive tender proposal...'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <Progress value={streamProgress || generationProgress} className="w-full" />
            <p className="text-sm text-gray-600 text-center">
              {streamMessage || generationStatus || "Initializing generation process..."}
            </p>
          </div>
        </DialogContent>
      </Dialog>

    </div>
  );
};

export default DocumentOverviewStep;
